import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Select,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';

const categoryOptions = [
  { value: 'primary', label: 'Primary' },
  { value: 'success', label: 'Success' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Danger' },
  { value: 'default', label: 'Dark' },
];

const colorMap = {
  primary: 'primary',
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
  default: 'default',
};

const CalendarPage = () => {
  const today = new Date();
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Add Event');
  const [eventTitle, setEventTitle] = useState('');
  const [eventCategory, setEventCategory] = useState('primary');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([
    { id: 1, title: 'All Day Event', start: new Date(), color: 'primary' },
  ]);

  const daysInMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate(),
    [today],
  );
  const firstDayIndex = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 1).getDay(),
    [today],
  );

  const weeks = useMemo(() => {
    const totalCells = Math.ceil((daysInMonth + firstDayIndex) / 7) * 7;
    return Array.from({ length: totalCells }, (_, idx) => {
      const dayNumber = idx - firstDayIndex + 1;
      const isValidDay = dayNumber > 0 && dayNumber <= daysInMonth;
      return isValidDay ? dayNumber : null;
    });
  }, [daysInMonth, firstDayIndex]);

  const handleNewEvent = (dayNumber) => {
    setModalTitle('Add New Event');
    setEventTitle(dayNumber ? `Event on ${dayNumber}` : '');
    setEventCategory('primary');
    setSelectedEvent(dayNumber ? { dayNumber } : null);
    setShowModal(true);
  };

  const handleEventClick = (event) => {
    setModalTitle('Edit Event');
    setEventTitle(event.title);
    setEventCategory(event.color || 'primary');
    setSelectedEvent(event);
    setShowModal(true);
  };

  const handleSaveEvent = (e) => {
    e.preventDefault();
    if (!eventTitle.trim()) return;

    if (selectedEvent && selectedEvent.id) {
      setEvents((prev) =>
        prev.map((evt) =>
          evt.id === selectedEvent.id
            ? { ...evt, title: eventTitle.trim(), color: eventCategory }
            : evt,
        ),
      );
    } else {
      const eventDate = selectedEvent?.dayNumber
        ? new Date(today.getFullYear(), today.getMonth(), selectedEvent.dayNumber)
        : new Date();
      const newEvent = {
        id: Date.now(),
        title: eventTitle.trim(),
        start: eventDate,
        color: eventCategory,
      };
      setEvents((prev) => [...prev, newEvent]);
    }
    setShowModal(false);
  };

  const handleDeleteEvent = () => {
    if (selectedEvent?.id) {
      setEvents((prev) => prev.filter((evt) => evt.id !== selectedEvent.id));
      setShowModal(false);
    }
  };

  const eventsForDay = (dayNumber) =>
    events.filter((evt) => {
      const date = new Date(evt.start);
      return (
        date.getDate() === dayNumber &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarDays size={20} />
          <Typography variant="h5">Calendar</Typography>
        </Stack>
        <Button startIcon={<Plus size={16} />} variant="contained" onClick={() => handleNewEvent()}>
          Create New Event
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Quick Add
              </Typography>
              <Stack spacing={1} mb={2}>
                {categoryOptions.slice(0, 4).map((evt) => (
                  <Button
                    key={evt.value}
                    variant="outlined"
                    onClick={() => {
                      setEventTitle(evt.label);
                      setEventCategory(evt.value);
                      setSelectedEvent(null);
                      setShowModal(true);
                    }}
                  >
                    {evt.label}
                  </Button>
                ))}
              </Stack>

              <Typography variant="subtitle1" gutterBottom>
                Recent activity
              </Typography>
              <Stack spacing={1}>
                {['Responded to need', 'Meeting with client', 'Updated template', 'Published campaign'].map(
                  (item, idx) => (
                    <Typography key={idx} variant="body2" color="text.secondary">
                      • {item}
                    </Typography>
                  ),
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {today.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click a day to add an event
                </Typography>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <TableCell key={day} align="center">
                          {day}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: weeks.length / 7 }).map((_, weekIdx) => (
                      <TableRow key={weekIdx} hover={false}>
                        {weeks.slice(weekIdx * 7, weekIdx * 7 + 7).map((dayNumber, idx) => {
                          const isToday = dayNumber === today.getDate();
                          const dayEvents = dayNumber ? eventsForDay(dayNumber) : [];
                          return (
                            <TableCell
                              key={idx}
                              sx={{
                                height: 100,
                                verticalAlign: 'top',
                                cursor: dayNumber ? 'pointer' : 'default',
                                bgcolor: isToday ? 'success.light' : 'background.paper',
                              }}
                              onClick={() => dayNumber && handleNewEvent(dayNumber)}
                            >
                              {dayNumber && (
                                <Stack spacing={0.5}>
                                  <Typography variant="subtitle2" color={isToday ? 'success.dark' : 'text.primary'}>
                                    {dayNumber}
                                  </Typography>
                                  {dayEvents.map((evt) => (
                                    <Chip
                                      key={evt.id}
                                      label={evt.title}
                                      size="small"
                                      color={colorMap[evt.color] || 'default'}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEventClick(evt);
                                      }}
                                    />
                                  ))}
                                </Stack>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{modalTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Event Name"
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
              fullWidth
              required
            />
            <Select
              value={eventCategory}
              onChange={(e) => setEventCategory(e.target.value)}
              fullWidth
              displayEmpty
            >
              {categoryOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </DialogContent>
        <DialogActions>
          {selectedEvent?.id && (
            <Button color="error" startIcon={<Trash2 size={16} />} onClick={handleDeleteEvent}>
              Delete
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="contained" onClick={handleSaveEvent} startIcon={<Plus size={16} />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CalendarPage;
